import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/lib/store";
import { DeliveryBadge, StatusBadge, fmtDate, inr } from "@/components/badges";
import InvoiceModal from "@/components/InvoiceModal";

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [invoiceId, setInvoiceId] = useState(null);
  const { addToCart } = useStore();

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const reorder = async (o) => {
    for (const it of o.items) {
      try {
        const { data: p } = await api.get(`/products/${it.product_id}`);
        addToCart(p, it.qty);
      } catch {
        toast.error(`${it.name} is no longer available`);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="orders-page">
      <p className="overline-label">Buyer</p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">My orders</h1>
      {orders.length === 0 ? (
        <p className="mt-12 text-center text-sm text-slate-500" data-testid="orders-empty">No orders yet. <Link to="/buyer" className="text-orange-700 font-semibold">Start sourcing</Link>.</p>
      ) : (
        <div className="mt-6 space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.order_no}`} className="card-surface p-4 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/orders/${o.id}`} className="font-mono2 text-sm font-bold text-slate-900 hover:text-orange-700">{o.order_no}</Link>
                  <StatusBadge status={o.status} />
                  <DeliveryBadge mode={o.mode} />
                </div>
                <p className="mt-1 text-xs text-slate-500">{fmtDate(o.created_at)} · {o.vendor_name} · {o.items.length} items</p>
                <p className="mt-0.5 text-xs text-slate-500 truncate">{o.items.map((i) => `${i.qty}× ${i.name}`).join(" · ")}</p>
              </div>
              <p className="font-display font-bold text-lg text-slate-900">{inr(o.total)}</p>
              <div className="flex gap-2">
                <Link to={`/orders/${o.id}`} data-testid={`track-${o.order_no}`} className="btn-ghost-b2b !px-3 !py-2 text-xs">Track</Link>
                <button data-testid={`invoice-${o.order_no}`} onClick={() => setInvoiceId(o.id)} className="btn-ghost-b2b !px-3 !py-2 text-xs"><FileText className="h-3.5 w-3.5" /> GST Invoice</button>
                <button data-testid={`reorder-btn-${o.order_no}`} onClick={() => reorder(o)} className="btn-dark !px-3 !py-2 text-xs"><RotateCcw className="h-3.5 w-3.5" /> Reorder</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {invoiceId && <InvoiceModal orderId={invoiceId} onClose={() => setInvoiceId(null)} />}
    </div>
  );
}
